import { Link } from "react-router-dom";
import {
  ArrowRight,
  MagnifyingGlass,
  Compass,
  FilePlus,
  Scales,
  ChartLineUp,
  Coins,
  Trademark,
} from "@phosphor-icons/react";
import PageHero from "@/components/site/PageHero";
import EnquiryForm from "@/components/site/EnquiryForm";
import { MaskedLinesOnScroll, Reveal, spotlightHandler } from "@/components/motion/Reveal";
import { useSeo } from "@/lib/useSeo";

const SERVICES = [
  {
    icon: MagnifyingGlass,
    title: "Patentability & prior-art search",
    body:
      "Know whether your invention is novel before you spend on drafting. Global patent and non-patent literature, with a clear opinion on what is likely to survive examination.",
  },
  {
    icon: Compass,
    title: "Freedom-to-operate (FTO) study",
    body:
      "Launch, raise or license with confidence. We identify blocking patents in your target markets and flag design-around options and residual risk in plain language.",
  },
  {
    icon: FilePlus,
    title: "Patent drafting",
    body:
      "Specifications and claims engineered for breadth and defensibility — across mechanical, electronics/AI, chemistry, pharma and medical devices.",
  },
  {
    icon: Scales,
    title: "Patent invalidity search",
    body:
      "Exhaustive prior-art sweeps and defensible claim charts for opposition, litigation or licensing — built to hold up under scrutiny.",
  },
  {
    icon: ChartLineUp,
    title: "Patent landscape analysis",
    body:
      "See where a technology is heading, who owns the white space, and where to focus R&D, filing and licensing strategy.",
  },
  {
    icon: Coins,
    title: "Patent valuation",
    body:
      "Defensible valuations for licensing, M&A, litigation or reporting — income, market and cost approaches, led by a Certified Patent Valuation Analyst.",
  },
  {
    icon: Trademark,
    title: "Trademark search & filing",
    body:
      "Availability searches and end-to-end filing — from class selection to registration — so your brand is protected from day one.",
  },
];

const PROOF = [
  ["CPVA", "Certified Patent Valuation Analyst"],
  ["50+", "Granted patents as inventor"],
  ["18+ yrs", "Global MNC experience"],
];

const FAQS = [
  {
    q: "What's the difference between a patentability search and an FTO search?",
    a: "A patentability (prior-art) search tells you whether your invention is novel and likely to be granted. A freedom-to-operate (FTO) search tells you whether making or selling your product might infringe someone else's live patents. They answer different questions and are usually done at different stages.",
  },
  {
    q: "How long does a search take?",
    a: "A typical search takes about 1–3 weeks depending on the technology and scope. If you're on a tighter timeline, expedited delivery in 1–3 days is available at a premium.",
  },
  {
    q: "Should I run a search before filing a provisional?",
    a: "Wherever possible, yes. A quick patentability search before a provisional helps you file stronger claims, avoid wasted spend, and understand the competitive landscape before you commit.",
  },
  {
    q: "What do I receive at the end?",
    a: "A clear, written report — the references found, an opinion on novelty or risk, and practical next steps. For invalidity and FTO work this includes claim charts mapping each reference to the specific claim elements.",
  },
];

function CtaBlock({ testid }) {
  return (
    <Reveal>
      <div
        data-testid={testid}
        className="border border-[#0F9D6B]/30 bg-navy-deep p-8 lg:p-10 flex flex-col md:flex-row md:items-center gap-6 md:justify-between"
      >
        <div>
          <h3 className="font-serif text-2xl lg:text-3xl text-[#F5F4F0]">Have a matter to discuss?</h3>
          <p className="mt-2 text-[#8A99AA] font-light max-w-xl">
            Tell us what you're working on and get a free 15-minute review from a CPVA.
          </p>
        </div>
        <Link
          to="#enquiry"
          data-testid={`${testid}-btn`}
          className="group relative inline-flex items-center gap-3 h-14 px-9 flex-shrink-0 bg-[#0F9D6B] text-[#F5F4F0] text-xs font-semibold tracking-[0.16em] uppercase overflow-hidden"
        >
          <span className="absolute inset-0 bg-[#14B981] scale-x-0 origin-left transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
          <span className="relative">Brief us on your matter</span>
          <ArrowRight size={15} weight="bold" className="relative transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </Reveal>
  );
}

export default function PatentServices() {
  useSeo(
    "Patent & Trademark Services — Patentability, FTO, Drafting | Gurukul of Ramana",
    "End-to-end patent and trademark services in India — patentability and FTO searches, drafting, invalidity, landscape, valuation and filing. CPVA-led, 50+ patents. Brief us on your matter.",
  );

  return (
    <div data-testid="patent-services-page" className="bg-navy">
      <PageHero
        testid="patent-services-hero"
        titleTestid="patent-services-title"
        overline="Patent and Trademark Services"
        lines={[<>Patent &amp; Trademark Services</>]}
        sub="CPVA-led IP practice with 50+ granted patents and 18+ years of global MNC experience — patentability and FTO searches, drafting, invalidity, landscape, valuation and trademark filing for startups, R&D teams and in-house counsel."
      >
        <Reveal delay={0.55} y={18}>
          <div className="mt-10">
            <Link
              to="#enquiry"
              data-testid="services-hero-cta"
              className="group relative inline-flex items-center gap-3 h-14 px-8 bg-[#0F9D6B] text-[#F5F4F0] text-xs font-semibold tracking-[0.14em] uppercase overflow-hidden"
            >
              <span className="absolute inset-0 bg-[#14B981] scale-x-0 origin-left transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
              <span className="relative">Brief us on your matter</span>
              <ArrowRight size={15} weight="bold" className="relative transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </PageHero>

      {/* OPENING */}
      <section data-testid="services-opening" className="bg-navy border-t border-[#C6A87C]/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-20 lg:py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-3">
            <Reveal>
              <div className="label-overline flex items-center gap-3">
                <span className="h-px w-8 bg-[#C6A87C]" />
                The practice
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-9">
            <MaskedLinesOnScroll
              className="text-2xl lg:text-3xl text-[#F5F4F0] leading-[1.35] font-light max-w-4xl"
              lines={[
                <>End-to-end patent and trademark support,</>,
                <>led by a Certified Patent Valuation Analyst.</>,
              ]}
            />
            <Reveal delay={0.15}>
              <p className="mt-8 text-lg text-[#8A99AA] font-light leading-relaxed max-w-3xl">
                Gurukul of Ramana is a CPVA-led IP practice with 50+ granted patents and 18+
                years of global MNC experience. We provide end-to-end patent and trademark
                support for startups, R&amp;D teams and in-house counsel — patentability and
                freedom-to-operate searches, patent drafting, invalidity, landscape analysis,
                valuation, and trademark search &amp; filing — delivered with the rigour of a
                large firm and the responsiveness of a boutique.
              </p>
            </Reveal>
            <div className="mt-10">
              <CtaBlock testid="services-cta-top" />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section data-testid="services-list" className="bg-navy-deep border-t border-[#C6A87C]/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-20 lg:py-28">
          <Reveal>
            <div className="label-overline flex items-center gap-3">
              <span className="h-px w-8 bg-[#C6A87C]" />
              What we do
            </div>
          </Reveal>
          <MaskedLinesOnScroll
            className="mt-5 font-serif text-4xl lg:text-5xl text-[#F5F4F0] tracking-tight"
            lines={[<>Seven services, one senior eye.</>]}
          />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={Math.min(i * 0.06, 0.3)}>
                  <article
                    data-testid={`service-card-${i}`}
                    onMouseMove={spotlightHandler}
                    className="spotlight-card h-full bg-navy border border-[#C6A87C]/15 p-8 transition-colors duration-500 hover:border-[#0F9D6B]/50"
                  >
                    <div className="flex items-start justify-between">
                      <span className="w-12 h-12 border border-[#C6A87C]/30 text-[#C6A87C] flex items-center justify-center">
                        <Icon size={22} weight="light" />
                      </span>
                      <span className="font-mono-tech text-xs uppercase tracking-[0.22em] text-[#8A99AA]">
                        {String(i + 1).padStart(2, "0")} / 07
                      </span>
                    </div>
                    <h3 className="mt-7 font-serif text-2xl text-[#F5F4F0] leading-tight">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-[#8A99AA] font-light leading-relaxed">{s.body}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY GURUKUL OF RAMANA */}
      <section data-testid="services-why" className="bg-navy border-t border-[#C6A87C]/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-20 lg:py-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="label-overline flex items-center gap-3">
                <span className="h-px w-8 bg-[#C6A87C]" />
                Why Gurukul of Ramana
              </div>
            </Reveal>
            <MaskedLinesOnScroll
              className="mt-5 font-serif text-3xl lg:text-4xl text-[#F5F4F0] leading-[1.1] tracking-tight"
              lines={[<>Credentials that</>, <>hold up.</>]}
            />
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <p className="text-lg text-[#8A99AA] font-light leading-relaxed max-w-3xl">
                Your work is reviewed by a Certified Patent Valuation Analyst and prolific
                inventor — not handed down a chain of juniors. That means opinions you can
                stand behind, drafting that survives examination, and searches built for the
                decisions they inform.
              </p>
            </Reveal>
            <div className="mt-12 grid sm:grid-cols-3 gap-8">
              {PROOF.map(([k, v], i) => (
                <Reveal key={k} delay={0.15 + i * 0.08}>
                  <div className="border-l border-[#C6A87C]/50 pl-5">
                    <div className="font-serif text-4xl lg:text-5xl text-[#C6A87C]">{k}</div>
                    <div className="label-overline mt-3 !text-[10px] text-[#8A99AA]">{v}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT TESTING */}
      <section data-testid="services-product-testing" className="bg-navy-deep border-t border-[#C6A87C]/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-20 lg:py-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="label-overline flex items-center gap-3">
                <span className="h-px w-8 bg-[#C6A87C]" />
                Engineering Services
              </div>
            </Reveal>
            <MaskedLinesOnScroll
              className="mt-5 font-serif text-4xl lg:text-5xl text-[#F5F4F0] leading-[1.08] tracking-tight"
              lines={[<>Product</>, <>Testing.</>]}
            />
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <p className="text-lg text-[#8A99AA] font-light leading-relaxed max-w-3xl">
                We provide end-to-end engineering product testing services designed to help
                companies validate performance, reliability, and safety before their products
                reach the market. From early-stage prototypes to production-ready units, our
                team applies rigorous testing protocols across mechanical, electrical, and
                functional domains — supporting clients across software, consumer goods, and
                enterprise industries. Whether you need stress testing, compliance
                verification, failure analysis, or full validation cycles, we deliver clear,
                data-backed insights that help you ship with confidence.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* REVERSE ENGINEERING */}
      <section data-testid="services-reverse-engineering" className="bg-navy border-t border-[#C6A87C]/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-20 lg:py-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="label-overline flex items-center gap-3">
                <span className="h-px w-8 bg-[#C6A87C]" />
                Engineering Services
              </div>
            </Reveal>
            <MaskedLinesOnScroll
              className="mt-5 font-serif text-4xl lg:text-5xl text-[#F5F4F0] leading-[1.08] tracking-tight"
              lines={[<>Reverse</>, <>Engineering.</>]}
            />
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <p className="text-lg text-[#8A99AA] font-light leading-relaxed max-w-3xl">
                We provide specialized Reverse Engineering services across a diverse range of
                domains, helping clients uncover, analyze, and reconstruct the inner workings
                of complex systems, products, and software. From mechanical components and
                electronic hardware to embedded firmware, software applications, and legacy
                systems, we deliver precise insights that support innovation, competitive
                analysis, legacy modernization, security assessment, and design optimization.
                Combining technical expertise with a meticulous, confidential approach, we
                turn existing technologies into actionable knowledge tailored to your specific
                goals.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ENQUIRY FORM */}
      <section id="enquiry" data-testid="services-enquiry" className="bg-navy-deep border-t border-[#C6A87C]/10 scroll-mt-32">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 lg:py-28">
          <Reveal>
            <div className="label-overline flex items-center gap-3">
              <span className="h-px w-8 bg-[#C6A87C]" />
              Enquiry
            </div>
          </Reveal>
          <MaskedLinesOnScroll
            className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F5F4F0] leading-[1.08] tracking-tight"
            lines={[<>Brief us on your matter.</>]}
          />
          <Reveal delay={0.12}>
            <p className="mt-6 text-[#8A99AA] font-light max-w-2xl text-lg leading-relaxed">
              Tell us what you're working on and get a free 15-minute review from a CPVA. We
              respond within one business day.
            </p>
          </Reveal>
          <div className="mt-10">
            <EnquiryForm />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="services-faq" className="bg-navy border-t border-[#C6A87C]/10">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 lg:py-28">
          <Reveal>
            <div className="label-overline flex items-center gap-3">
              <span className="h-px w-8 bg-[#C6A87C]" />
              Frequently asked
            </div>
          </Reveal>
          <MaskedLinesOnScroll
            className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F5F4F0] tracking-tight"
            lines={[<>Questions, answered.</>]}
          />
          <div className="mt-12 divide-y divide-[#C6A87C]/12 border-t border-[#C6A87C]/12">
            {FAQS.map((f, i) => (
              <Reveal key={i} delay={Math.min(i * 0.06, 0.24)}>
                <div data-testid={`faq-${i}`} className="py-8 grid md:grid-cols-12 gap-4 md:gap-8">
                  <h3 className="md:col-span-5 font-serif text-xl lg:text-2xl text-[#F5F4F0] leading-snug">
                    {f.q}
                  </h3>
                  <p className="md:col-span-7 text-[#8A99AA] font-light leading-relaxed">
                    {f.a}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section data-testid="services-final-cta" className="bg-abyss border-t border-[#C6A87C]/10 relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay opacity-[0.1] pointer-events-none" />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-20 lg:py-24">
          <CtaBlock testid="services-cta-bottom" />
        </div>
      </section>
    </div>
  );
}
